import { queryOptions, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useApi } from '../../api/api-context';
import { useBudgetId } from '../../budget/budget-store';
import { AccountState, AccountType } from './account-types';
/**
 * Fetches accounts for a given budget ID.
 *
 * @param {string} budgetId - The ID of the budget.
 * @param {ReturnType<typeof useApi>} api - API instance for making requests.
 * @returns {Promise<Account[]>} - A promise resolving to the list of accounts.
 */
const fetchAccounts = async (budgetId, api) => {
    const response = await api.get(`/account/budget/${budgetId}`);
    return response.data;
};
/**
 * Exposed query options for fetching accounts.
 *
 * @param {string} budgetId - The ID of the budget.
 * @param {ReturnType<typeof useApi>} api - API instance.
 * @returns Query options for React Query.
 */
export const getAccountsQueryOptions = (budgetId, api) => queryOptions({
    queryKey: ['accounts', budgetId],
    queryFn: () => fetchAccounts(budgetId, api),
});
/**
 * React Query hook for fetching accounts based on a budget ID.
 *
 * @param {string} budgetId - The ID of the budget.
 * @param {UseAccountsOptions} options - Optional configuration for the query.
 * @returns React Query response object.
 */
export const useGetAccountsApi = (budgetId, { queryConfig } = {}) => {
    const api = useApi();
    return useQuery({
        ...getAccountsQueryOptions(budgetId, api),
        ...queryConfig,
    });
};
/**
 * Hook to get accounts for the currently selected budget.
 *
 * @returns {Account[]} - The list of accounts for the current budget.
 */
export const useAccounts = () => {
    const budgetId = useBudgetId();
    const { data: accounts = [] } = useGetAccountsApi(budgetId);
    return accounts;
};
/**
 * Hook to create a map of accounts by their ID.
 *
 * @returns {Map<string, Account>} - A map where account IDs are keys and Account objects are values.
 */
export const useAccountsMap = () => {
    const accounts = useAccounts();
    return useMemo(() => new Map(accounts.map((account) => [account.id, account])), [accounts]);
};
/**
 * Returns a filtered list of liquid accounts (active non-credit card accounts).
 *
 * Liquid accounts include:
 * - Checking accounts
 * - Savings accounts
 * - Cash accounts
 * - Any active accounts that aren't credit cards or off-budget
 *
 * @returns {Account[]} Array of active liquid accounts
 */
export function useLiquidAccounts() {
    const accounts = useAccounts();
    return useMemo(() => {
        return accounts.filter((account) => account.state === AccountState.ACTIVE &&
            account.type !== AccountType.CREDIT_CARD &&
            account.type !== AccountType.OFF_BUDGET_ASSET &&
            account.type !== AccountType.OFF_BUDGET_LIABILITY);
    }, [accounts]);
}
/**
 * Returns a filtered list of active credit card accounts.
 *
 * This hook filters the accounts to only include:
 * - Accounts with type CREDIT_CARD
 * - Accounts with state ACTIVE
 *
 * @returns {Account[]} Array of active credit card accounts
 */
export function useCreditCardAccounts() {
    const accounts = useAccounts();
    return useMemo(() => {
        return accounts.filter((account) => account.state === AccountState.ACTIVE && account.type === AccountType.CREDIT_CARD);
    }, [accounts]);
}
/**
 * Returns a filtered list of archived accounts.
 *
 * This hook filters the accounts to only include:
 * - Accounts with state ARCHIVED
 *
 * @returns {Account[]} Array of active credit card accounts
 */
export function useArchivedAccounts() {
    const accounts = useAccounts();
    return useMemo(() => {
        return accounts.filter((account) => account.state === AccountState.ARCHIVED);
    }, [accounts]);
}
/**
 * Returns a filtered list of active off budget accounts.
 *
 * This hook filters the accounts to only include:
 * - Accounts with type OFF_BUDGET
 * - Accounts with state ACTIVE
 *
 * @returns {Account[]} Array of active credit card accounts
 */
export function useOffBudgetAccounts() {
    const accounts = useAccounts();
    return useMemo(() => {
        return accounts.filter((account) => account.state === AccountState.ACTIVE &&
            (account.type === AccountType.OFF_BUDGET_ASSET || account.type === AccountType.OFF_BUDGET_LIABILITY));
    }, [accounts]);
}
/**
 * Returns whether the specified account is an off-budget account.
 *
 * This hook checks if the account with the given ID is an off-budget account
 * by verifying if its type is either OFF_BUDGET_ASSET or OFF_BUDGET_LIABILITY.
 *
 * @param accountId - The ID of the account to check
 * @returns {boolean} True if the account is off-budget, false otherwise
 */
export function useIsOffBudget(accountId) {
    const accounts = useAccounts();
    return useMemo(() => {
        const account = accounts.find((acc) => acc.id === accountId);
        return (account?.type === AccountType.OFF_BUDGET_ASSET || account?.type === AccountType.OFF_BUDGET_LIABILITY || false);
    }, [accounts, accountId]);
}
